import { useReducer, useMemo, useCallback } from 'react';
import { posNameKey } from '../utils/posNameKey';

/**
 * ============================================================================
 * BANQUET ENTRY — the quantity cascade
 * ============================================================================
 *
 * A banquet is billed plates x rate, and most items go out at the plate count:
 * 500 guests, 500 plates of butter chicken. So the plate count DRIVES the item
 * quantities — except the ones the user has deliberately changed. "500 plates,
 * but only 200 gulab jamun" has to survive the client ringing back to say the
 * guest list is now 600.
 *
 * Every row therefore carries a quantitySource:
 *
 *   inherited  the quantity equals the plate count and was never hand-edited,
 *              so it follows when the plate count changes.
 *   override   the user typed something else, so it stays put.
 *
 * A row moves between the two ONLY by user action — typing a different number
 * pins it, typing the plate count back releases it, and the reset control
 * releases it explicitly. Nothing else may reclassify a row, because the flag
 * is a record of the user's intent and inferring it from anything else is how
 * a deliberate figure gets silently overwritten.
 *
 * The reducer is exported separately from the hook so the whole matrix can be
 * tested without React. The server re-derives quantitySource on submit and
 * never trusts what we send; this flag exists to drive the UI, not the record.
 */

/** Quantities are stored NUMERIC(10,3). */
const round3 = (n) => Math.round(Number(n) * 1000) / 1000;

export const initialBanquetState = {
  functionName: '',
  platesOrdered: null,   // positive integer, or null while unknown
  ratePerPlate: null,    // non-negative number, or null while unknown
  rows: [],              // [{ rowId, menuItemId, itemName, quantity, quantitySource, notInMenu? }]
  nextRowId: 1,
  /** { updated, kept } after a plate change, for the one-line confirmation. */
  lastCascade: null,
  /** Transient message for a refused action (a duplicate item). */
  error: null,
};

/** A plate count is a whole number of plates or it is unknown. No coercion. */
const asPlateCount = (v) => (Number.isInteger(v) && v >= 1 ? v : null);

/** A quantity box may legitimately be empty mid-edit, which is null, not 0. */
const asQuantity = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? round3(n) : null;
};

export function banquetReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD': {
      // functionName is free text; ratePerPlate is a number or null.
      const value = action.field === 'ratePerPlate'
        ? (action.value === null || action.value === undefined || action.value === ''
          ? null
          : (Number.isFinite(Number(action.value)) ? Number(action.value) : null))
        : action.value;
      return { ...state, [action.field]: value, error: null };
    }

    case 'SET_PLATES': {
      const plates = asPlateCount(action.value);
      if (plates === state.platesOrdered) {
        // Not a change, so not a cascade. Reporting "0 updated" here would
        // flash a confirmation line at someone who only re-typed the number.
        return { ...state, lastCascade: null, error: null };
      }

      let updated = 0;
      let kept = 0;
      const rows = state.rows.map((r) => {
        if (r.quantitySource !== 'inherited') { kept += 1; return r; }
        updated += 1;
        // Rows are KEPT when the count is cleared, not dropped: the user is
        // mid-edit, and deleting their item list to punish a blank box would
        // be the most destructive possible reading of an empty field.
        return { ...r, quantity: plates };
      });

      return { ...state, platesOrdered: plates, rows, lastCascade: { updated, kept }, error: null };
    }

    case 'ADD_EMPTY_ROW': {
      return {
        ...state,
        rows: [...state.rows, {
          rowId: state.nextRowId,
          menuItemId: null,
          itemName: '',
          quantity: state.platesOrdered,
          quantitySource: 'inherited',
        }],
        nextRowId: state.nextRowId + 1,
        error: null,
      };
    }

    case 'UPDATE_ROW_ITEM': {
      const { rowId, menuItemId, itemName } = action;
      if (menuItemId && state.rows.some((r) => r.rowId !== rowId && r.menuItemId === menuItemId)) {
        return { ...state, error: `${itemName || 'That item'} is already on this banquet.` };
      }
      return {
        ...state,
        rows: state.rows.map((r) => (
          r.rowId === rowId
            // A new name or a picked dish is a new answer: a "Not in Menu" mark is dropped.
            ? { ...r, menuItemId: menuItemId || null, itemName: itemName || '', notInMenu: false }
            : r
        )),
        error: null,
      };
    }

    // A dish not in Item Mapping (spec 2026-10-26-tsr-add-as-not-in-menu): kept by
    // name and plates only — no money, the bill is plates × rate — and recorded as a
    // Not in Menu line. The same name twice (same name key) is refused, as a dish is.
    case 'MARK_NOT_IN_MENU': {
      const { rowId } = action;
      const itemName = String(action.itemName || '').trim();
      const key = posNameKey(itemName);
      if (!key) return state;
      if (state.rows.some((r) => r.rowId !== rowId && r.notInMenu && posNameKey(r.itemName) === key)) {
        return { ...state, error: `${itemName} is already on this banquet.` };
      }
      return {
        ...state,
        rows: state.rows.map((r) => (r.rowId === rowId ? { ...r, menuItemId: null, itemName, notInMenu: true } : r)),
        error: null,
      };
    }

    case 'ADD_ITEM': {
      const menuItemId = action.item?.menuItemId;
      if (!Number.isInteger(menuItemId) || menuItemId <= 0) return state;
      // Nothing to inherit yet — the UI disables the picker, and this is the
      // guard behind it.
      if (state.platesOrdered === null) return state;

      if (state.rows.some((r) => r.menuItemId === menuItemId)) {
        // Refused rather than merged: the existing row may carry an override,
        // and quietly adding to it would destroy a number the user chose.
        return { ...state, error: `${action.item.itemName || 'That item'} is already on this banquet.` };
      }

      // If an empty row already exists, fill it in
      const emptyRowIndex = state.rows.findIndex((r) => !r.menuItemId && (!r.itemName || r.itemName.trim() === ''));
      if (emptyRowIndex !== -1) {
        const rows = [...state.rows];
        rows[emptyRowIndex] = {
          ...rows[emptyRowIndex],
          menuItemId,
          itemName: action.item.itemName || '',
        };
        return { ...state, rows, error: null };
      }

      return {
        ...state,
        rows: [...state.rows, {
          rowId: state.nextRowId,
          menuItemId,
          itemName: action.item.itemName || '',
          quantity: state.platesOrdered,
          quantitySource: 'inherited',
        }],
        nextRowId: state.nextRowId + 1,
        error: null,
      };
    }

    case 'SET_ROW_QTY': {
      const quantity = asQuantity(action.value);
      return {
        ...state,
        rows: state.rows.map((r) => {
          if (r.rowId !== action.rowId) return r;
          // Exactly the plate count means the user has typed their way back to
          // the default, so the row is released. A blank box is an override:
          // it is certainly not the plate count, and leaving it inherited
          // would refill it the moment the plate count moved.
          const source = quantity !== null && quantity === state.platesOrdered ? 'inherited' : 'override';
          return { ...r, quantity, quantitySource: source };
        }),
        error: null,
      };
    }

    case 'RESET_ROW_QTY': {
      if (state.platesOrdered === null) return state;
      return {
        ...state,
        rows: state.rows.map((r) => (
          r.rowId === action.rowId
            ? { ...r, quantity: state.platesOrdered, quantitySource: 'inherited' }
            : r
        )),
        error: null,
      };
    }

    case 'REMOVE_ROW':
      return { ...state, rows: state.rows.filter((r) => r.rowId !== action.rowId), error: null };

    case 'CLEAR_ERROR':
      return state.error === null ? state : { ...state, error: null };

    case 'RESET_ALL':
      return initialBanquetState;

    default:
      return state;
  }
}

/**
 * Everything computed FROM the state, in one place.
 *
 * Derived rather than stored, for the same reason totalPlatesLabel is derived
 * on the TSR page: a second copy of a number is a second thing to remember to
 * update, and it drifts the first time someone forgets.
 */
export function deriveBanquet(state) {
  const { functionName, platesOrdered, ratePerPlate, rows } = state;

  const totalBill = platesOrdered !== null && ratePerPlate !== null
    ? Math.round(platesOrdered * ratePerPlate * 100) / 100
    : null;

  const canAddItems = platesOrdered !== null;

  const validItemRows = rows.filter((r) => r.menuItemId || r.notInMenu);
  const everyRowHasAQuantity = validItemRows.every((r) => typeof r.quantity === 'number' && r.quantity > 0);
  const hasUnselectedDraft = rows.some((r) => !r.menuItemId && !r.notInMenu && r.itemName && r.itemName.trim().length > 0);

  const canSubmit = Boolean(
    String(functionName || '').trim().length > 0
    && platesOrdered !== null
    // A fully sponsored function is real, so zero is a rate; null is not.
    && ratePerPlate !== null && ratePerPlate >= 0
    && everyRowHasAQuantity
    && !hasUnselectedDraft
  );

  return {
    totalBill,
    canAddItems,
    canSubmit,
    overrideCount: rows.filter((r) => r.quantitySource === 'override').length,
    // What /submit accepts. quantitySource is deliberately NOT sent: the server
    // re-derives it, and including it would imply it is the client's to decide.
    payloadItems: validItemRows.filter((r) => r.menuItemId).map((r) => ({ menuItemId: r.menuItemId, quantity: r.quantity })),
    // Dishes not in Item Mapping, sent apart: name and plates only.
    payloadNotInMenu: validItemRows.filter((r) => r.notInMenu).map((r) => ({ itemName: r.itemName, quantity: r.quantity })),
  };
}

/**
 * The hook the entry screen uses. State plus named actions, so a component
 * never dispatches a raw object and no action name is spelled in two places.
 */
export default function useBanquetEntry() {
  const [state, dispatch] = useReducer(banquetReducer, initialBanquetState);
  const derived = useMemo(() => deriveBanquet(state), [state]);

  const actions = useMemo(() => ({
    setField: (field, value) => dispatch({ type: 'SET_FIELD', field, value }),
    setPlates: (value) => dispatch({ type: 'SET_PLATES', value }),
    addItem: (item) => dispatch({ type: 'ADD_ITEM', item }),
    addEmptyRow: () => dispatch({ type: 'ADD_EMPTY_ROW' }),
    updateRowItem: (rowId, menuItemId, itemName) => dispatch({ type: 'UPDATE_ROW_ITEM', rowId, menuItemId, itemName }),
    markNotInMenu: (rowId, itemName) => dispatch({ type: 'MARK_NOT_IN_MENU', rowId, itemName }),
    setRowQty: (rowId, value) => dispatch({ type: 'SET_ROW_QTY', rowId, value }),
    resetRowQty: (rowId) => dispatch({ type: 'RESET_ROW_QTY', rowId }),
    removeRow: (rowId) => dispatch({ type: 'REMOVE_ROW', rowId }),
    clearError: () => dispatch({ type: 'CLEAR_ERROR' }),
    reset: () => dispatch({ type: 'RESET_ALL' }),
  }), []);

  const reset = useCallback(() => dispatch({ type: 'RESET_ALL' }), []);

  return { state, ...derived, ...actions, reset };
}
